import { KEAElement, KEALink, KEAPaper, KEAUMLClass, LinkLabelPosition } from "@kea-mod/jointjs";
import { InteractionType } from "context/KEAGraphContext";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

export type InlineRenameTarget =
  // initial carries the keystroke that opened the editor, so that character is not lost.
  | { paper: KEAPaper; element: joint.dia.Element; initial?: string }
  | { paper: KEAPaper; linkView: joint.dia.LinkView; position: LinkLabelPosition };

interface Props {
  target: InlineRenameTarget;
  onClose: () => void;
  pushPresent: () => void;
  addUserInteraction: (interactionType: InteractionType, interactionTime: number, payload?: any) => void;
}

const MIN_WIDTH = 90;

// The UML class does not keep its name as a label but separately from the identifier.
const readName = (element: joint.dia.Element): string =>
  element.get("type") === "kea.UMLClass"
    ? (element as unknown as KEAUMLClass).getClassName()
    : (element as unknown as KEAElement).getLabel();

const writeName = (element: joint.dia.Element, value: string): void => {
  if (element.get("type") === "kea.UMLClass") (element as unknown as KEAUMLClass).setClassName(value);
  else (element as unknown as KEAElement).setLabel(value);
};

// The same ratios KEALink.getLabelMarkup uses to draw the labels.
export const LABEL_RATIO: Record<LinkLabelPosition, number> = {
  [LinkLabelPosition.SOURCE]: 0.1,
  [LinkLabelPosition.MID]: 0.5,
  [LinkLabelPosition.TARGET]: 0.9,
};

const readLabel = (link: KEALink, position: LinkLabelPosition): string =>
  link.getKEALabel(position)?.value ?? "";

const writeLabel = (link: KEALink, position: LinkLabelPosition, value: string): void => {
  if (value.trim() === "") link.deleteKEALabel({ position, value });
  else link.setKEALabel({ position, value });
};

export const InlineLabelEditor = ({ target, onClose, pushPresent, addUserInteraction }: Props): JSX.Element => {
  const { paper } = target;
  const inputRef = useRef<HTMLInputElement>(null);

  const read = (): string =>
    "linkView" in target
      ? readLabel(target.linkView.model as KEALink, target.position)
      : readName(target.element) ?? "";

  const initial = "initial" in target ? target.initial : undefined;
  const [value, setValue] = useState<string>(() => initial ?? read());

  let left: number;
  let top: number;
  let width: number;

  if ("linkView" in target) {
    const point = target.linkView.getPointAtRatio(LABEL_RATIO[target.position]);
    const at = paper.localToPaperPoint(point.x, point.y);
    width = MIN_WIDTH;
    left = at.x - width / 2;
    top = at.y - 12;
  } else {
    const scale = paper.scale().sx;
    const bbox = target.element.getBBox();
    const topLeft = paper.localToPaperPoint(bbox.x, bbox.y);
    width = Math.max(bbox.width * scale, MIN_WIDTH);
    const height = Math.max(bbox.height * scale, 22);
    left = topLeft.x + (bbox.width * scale - width) / 2;
    top = topLeft.y + (height - 24) / 2;
  }

  useLayoutEffect(() => {
    inputRef.current?.focus();
    // With a first character already typed the cursor belongs behind it, not over it.
    if (initial === undefined) inputRef.current?.select();
    else inputRef.current?.setSelectionRange(initial.length, initial.length);
  }, []);

  useEffect(() => {
    paper.removeDeleteEvent();
    return () => paper.addDeleteEvent();
  }, [paper]);

  // A click on the canvas fires no blur because JointJS intercepts pointerdown.
  useEffect(() => {
    const outside = (e: MouseEvent) => {
      if (!inputRef.current?.contains(e.target as Node)) commitRef.current();
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  const commitRef = useRef<() => void>(() => {});

  const commit = () => {
    if (value !== read()) {
      if ("linkView" in target) writeLabel(target.linkView.model as KEALink, target.position, value);
      else writeName(target.element, value);
      pushPresent();
      const edited = "linkView" in target ? target.linkView.model : target.element;
      addUserInteraction(InteractionType.NODE_EDITING_SAVE, Date.now(), edited.toJSON());
    }
    onClose();
  };
  commitRef.current = commit;

  return (
    <input
      ref={inputRef}
      className="input is-small"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") commit();
        if (e.key === "Escape") onClose();
      }}
      style={{
        position: "absolute",
        left,
        top,
        width,
        height: 24,
        textAlign: "center",
        zIndex: 10,
      }}
    />
  );
};
